import { LocationQueryRaw, RouteLocationRaw } from "vue-router";
import { EMPTY_PARAM } from "@/shared/environment/ability-route-helper";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases";
import { SearchContext } from "@/shared/search/search.types";
import { RouteElementWithParam } from "@/shared/environment/ability.types";
import { useAbilityRoute } from "@/shared/environment/ability-route";
import { DocumentationType, Highlights } from "@/shared/documentation/documentation.types.ts";

export interface ContentNode {
    __typename: DocumentationType;
    id: string;
    contentId: string;
}

export type PathOptions = {
    highlights?: Highlights;
    context?: SearchContext;
    products?: string[];
};

export const useDocumentRoute = () => {
    const { getPathToChild, getPathToChildrenWithoutParents, getRouteElementsBySearchContext } = useAbilityRoute();

    const getPathToDocumentationDetailPage = (
        contentMapId: string,
        topicId: string,
        queryParams?: LocationQueryRaw
    ): RouteLocationRaw => {
        return getPathToChild(
            CommonRouteAliases.documentationDetail,
            {
                contentMapId,
                topicId,
            },
            queryParams
        );
    };

    const getContentPath = (content: ContentNode, context?: SearchContext) => {
        const isTopic = content.__typename === "Topic";
        const splitId = content.id.split("@");

        if (isTopic) {
            if (!splitId) return;
            return getPathToDocumentationDetailPageForSearch(splitId[1], splitId[0], { context });
        } else {
            return getPathToDocumentationDetailPageForSearch(content.contentId!, EMPTY_PARAM, { context });
        }
    };

    const getDocumentationDetailRouteElements = (
        contentMapId: string,
        topicId: string | undefined,
        options?: PathOptions
    ): RouteElementWithParam[] => {
        const topicIdParam = topicId ?? EMPTY_PARAM;
        const routeElements: RouteElementWithParam[] = [];
        const { context, products } = options || {};

        if (context?.product && products && products.length > 0) {
            routeElements.push({
                alias: CommonRouteAliases.product,
                namedParams: { [CommonRouteAliases.product]: context.product },
            });

            if (context?.asset) {
                routeElements.push({
                    alias: CommonRouteAliases.asset,
                    namedParams: { [CommonRouteAliases.asset]: context.asset },
                });
            }
        }

        if (context?.product || context?.asset || context?.article || context?.mechanic) {
            routeElements.push({
                alias: CommonRouteAliases.documentations,
                namedParams: {},
            });
        }

        routeElements.push({
            alias: CommonRouteAliases.documentationDetail,
            namedParams: { contentMapId, topicId: topicIdParam },
        });

        return routeElements;
    };

    const getPathToDocumentationDetailPageForSearch = (
        contentMapId: string,
        topicId: string | undefined,
        options?: PathOptions
    ): RouteLocationRaw => {
        const routeElements: RouteElementWithParam[] = getDocumentationDetailRouteElements(
            contentMapId,
            topicId,
            options
        );

        if (options?.highlights && (options?.highlights.title || options?.highlights.text))
            return getPathToChildrenWithoutParents(routeElements, {
                highlights: options?.highlights.text?.join(",") ?? [],
                title: options?.highlights.title ?? "",
            });

        return getPathToChildrenWithoutParents(routeElements);
    };

    const getPathToDocumentations = (context: SearchContext, queryParams?: Record<string, string>) => {
        const routeElements: RouteElementWithParam[] = getRouteElementsBySearchContext(context);

        routeElements.push({
            alias: CommonRouteAliases.documentations,
            namedParams: {},
        });

        if (!queryParams || Object.keys(queryParams).length === 0) {
            return getPathToChildrenWithoutParents(routeElements);
        }

        return getPathToChildrenWithoutParents(routeElements, queryParams);
    };

    return {
        getPathToDocumentationDetailPage,
        getContentPath,
        getPathToDocumentationDetailPageForSearch,
        getPathToDocumentations,
    };
};
